import {
  chakra,
  CloseButton,
  Drawer,
  Flex,
  Portal,
} from '@chakra-ui/react';
import React from 'react';
import type { ReactNode } from 'react';
import { HiOutlineChevronLeft } from 'react-icons/hi2';
import { useA11yAnnounce } from './AccessibilityAnnounce';

export interface DrawerComponentProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  footer?: ReactNode;
  children: ReactNode;
  closeOnOutside?: boolean;
  closeOnEsc?: boolean;
  drawerContentRef?: React.Ref<HTMLDivElement>;
  e2eTitle?: string;
  /**
   * Optional back-button affordance shown to the left of the title.
   * When provided, renders a chevron-left button in the header that calls
   * this handler. Use for multi-step drawer flows (e.g. picker → form).
   */
  onBack?: () => void;
  /**
   * Optional ReactNode rendered immediately after the title in the header,
   * e.g. a help / runbook info link. Kept additive so existing call sites
   * that only pass a string `title` are unaffected.
   */
  titleAdornment?: ReactNode;
  /**
   * Edge the drawer slides in from. Defaults to `'end'` (right side).
   * `'start'` opens from the left; `'top'` / `'bottom'` open as sheets.
   * Forwarded to `Drawer.Root`.
   */
  placement?: 'start' | 'end' | 'top' | 'bottom';
  /**
   * Alias for `closeOnOutside`, matching the legacy SettingsDrawer prop name
   * so call sites migrating off SettingsDrawer keep working without
   * a rename. When both are passed, `closeOnOutside` wins. Defaults to the
   * `closeOnOutside` value (true).
   */
  closeOnOverlayClick?: boolean;
  /**
   * When provided, wraps the drawer body AND footer in a `<form>` element with
   * these props (e.g. `onSubmit`), so a footer submit button (`type="submit"`,
   * or a `<DrawerFormFooter formId>`) submits the form. Mirrors SettingsDrawer's
   * form wrapper. When absent, no form element is rendered.
   */
  formProps?: React.FormHTMLAttributes<HTMLFormElement>;
  /**
   * Raw footer escape-hatch. Rendered inside `Drawer.Footer` exactly as given,
   * with NO default chrome beyond the footer region itself — use this when the
   * footer needs full custom layout rather than the `footer` slot's
   * `<DrawerFormFooter>` convention. Takes precedence over `footer` when both
   * are supplied. Mirrors SettingsDrawer's `customFooter`.
   */
  customFooter?: ReactNode;
}

/**
 * @useFor the canonical right-side drawer for forms (Edit, Create)
 *   and detail panels. Header with title + close button, scrollable
 *   body, sticky footer slot.
 *
 *   Examples in-tree: SMS People → Edit Person; AIM Onboarding →
 *   Edit Job; Roster → Add Student.
 *
 *   Pair with `<DrawerFormFooter>` for the action-row at the bottom
 *   (Save / Cancel / Delete). For full-screen forms use a route
 *   instead of a drawer; for confirmation modals use Chakra
 *   `<Dialog>` directly — drawer is specifically for "side panel
 *   over the current page" semantics.
 *
 *   Size scale (pick by content category; the px is the desktop,
 *   md-and-up, capped width — below the md breakpoint every size is
 *   full-bleed, so mobile is handled automatically):
 *     - `xs` (~320px) — menus / pickers / single-action drawers
 *       (e.g. OptionsDrawer, global search).
 *     - `sm` (~448px) — single-column form. DEFAULT, and the FLOOR
 *       for forms and detail panels: never go below `sm` for those.
 *     - `md` (~512px) — two-column or list-bearing form.
 *     - `lg` (~672px) — detail panel with rich content.
 *     - `xl` (~896px) — wide / master-detail (e.g. an event detail
 *       view).
 *
 *   The minimum width is the `sm` size-token floor plus the
 *   `base: 'full'` mobile behavior — do NOT add a raw `minWidth` in
 *   px, which reintroduces horizontal scrolling and breaks WCAG 2.1
 *   SC 1.4.10 (Reflow) on phones. See `libs/themes/aegix/CONVENTIONS.md`
 *   §Drawer sizes (the row map there mirrors this block; keep them
 *   consistent).
 */
export const DrawerComponent: React.FC<DrawerComponentProps> = ({
  isOpen,
  onClose,
  title,
  size = 'sm',
  footer,
  children,
  // No destructuring default here on purpose: leaving it `undefined` when the
  // caller omits it lets the `closeOnOverlayClick` alias take effect below.
  closeOnOutside,
  closeOnEsc = true,
  drawerContentRef,
  e2eTitle,
  onBack,
  titleAdornment,
  placement = 'end',
  closeOnOverlayClick,
  formProps,
  customFooter,
}) => {
  // Accessibility announcer (ADR-057 / ADR-091). No-op unless an app mounts
  // <AccessibilityAnnounceProvider> above, so without a provider the drawer
  // is silent. Closing announces 'close'; the back control announces 'back'.
  const announce = useA11yAnnounce();

  // `customFooter` is the raw escape-hatch and wins over the `footer` slot.
  const footerContent = customFooter ?? footer;

  // SettingsDrawer-parity: `closeOnOverlayClick` is the legacy alias; the
  // canonical `closeOnOutside` takes precedence when both are provided.
  const resolvedCloseOnOutside = closeOnOutside ?? closeOnOverlayClick ?? true;

  // Body + footer, optionally wrapped in a <form> so a footer submit button
  // (type="submit" / <DrawerFormFooter formId>) submits via `formProps`.
  // Mirrors SettingsDrawer's structure where the form encloses both regions.
  const body = (
    <>
      <Drawer.Body overflowY="auto" css={{ scrollbarGutter: 'stable' }}>
        {children}
      </Drawer.Body>

      {footerContent && <Drawer.Footer>{footerContent}</Drawer.Footer>}
    </>
  );

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open) {
          onClose();
          announce('close');
        }
      }}
      placement={placement}
      // WCAG 2.1 SC 1.4.10 (Reflow): full-bleed below the md breakpoint so the
      // drawer reflows to the full viewport width on phones; the capped inset
      // card is preserved at md and up. See ADR-089.
      size={{ base: 'full', md: size }}
      closeOnInteractOutside={resolvedCloseOnOutside}
      closeOnEscape={closeOnEsc}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content
            margin={{ base: 0, md: '10px' }}
            borderRadius={{ base: 0, md: 'lg' }}
            bg="surface.primary"
            ref={drawerContentRef}
            data-e2e={e2eTitle}
            role="dialog"
            aria-labelledby={title ? 'drawer-title' : undefined}
          >
            <Drawer.Header
              borderBottom="1px solid"
              borderColor="border.secondary"
            >
              <Drawer.Title color="text.primary" id="drawer-title" tabIndex={-1}>
                <Flex alignItems="center" gap={2}>
                  {onBack && (
                    <Flex
                      as="button"
                      // @ts-expect-error - Flex `as` doesn't carry through button props
                      type="button"
                      onClick={() => {
                        onBack();
                        announce('back');
                      }}
                      cursor="pointer"
                      color="text.primary"
                      fontSize="20px"
                      alignItems="center"
                      bg="transparent"
                      border="none"
                      p={0}
                      _hover={{ color: 'text.secondary' }}
                      data-e2e="drawerBack"
                      aria-label="Back"
                    >
                      <HiOutlineChevronLeft />
                    </Flex>
                  )}
                  {title}
                  {titleAdornment}
                </Flex>
              </Drawer.Title>
            </Drawer.Header>

            {formProps ? (
              <chakra.form
                {...formProps}
                display="flex"
                flexDirection="column"
                flex="1"
                overflow="hidden"
                data-e2e="drawerForm"
              >
                {body}
              </chakra.form>
            ) : (
              body
            )}

            <Drawer.CloseTrigger asChild>
              <CloseButton size="sm" aria-label="Close drawer" />
            </Drawer.CloseTrigger>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};
